/**
 * NetzwerkGraph - Interaktive Graph-Visualisierung
 * Zeigt Betreute, Institutionen und natürliche Personen als Knoten,
 * Beziehungen als Linien. Farbcodiert nach Typ, klickbar mit Detail-Popup.
 * NEU: Filter nach Beziehungsart und Knotentyp
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  RefreshCw,
  Users,
  Building2,
  UserCircle,
  X,
  Eye,
  Download,
  Settings2,
  RotateCcw,
  Filter,
  Heart,
  Banknote,
  Scale,
  Home,
  Briefcase,
  Stethoscope,
  Phone,
  Link2,
  ChevronDown,
  Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { exportGraphAsPNG, exportGraphAsSVG } from '@/lib/graphExport';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Typ-Konfiguration
const TYP_CONFIG = {
  klient: {
    color: '#3B82F6',
    icon: Users,
    label: 'Betreuter',
    bgClass: 'bg-blue-100 text-blue-700'
  },
  juristische_person: {
    color: '#14B8A6',
    icon: Building2,
    label: 'Institution',
    bgClass: 'bg-teal-100 text-teal-700'
  },
  natuerliche_person: {
    color: '#A855F7',
    icon: UserCircle,
    label: 'Person',
    bgClass: 'bg-purple-100 text-purple-700'
  }
};

// Beziehungsarten mit Icons und Farben
const BEZIEHUNG_CONFIG = {
  versichert_bei: { label: 'Versichert', color: '#EC4899', icon: Heart, group: 'institution' },
  leistung_von: { label: 'Leistungsbezug', color: '#22C55E', icon: Banknote, group: 'institution' },
  schulden_bei: { label: 'Schulden', color: '#EF4444', icon: Scale, group: 'institution' },
  wohnt_in: { label: 'Untergebracht', color: '#3B82F6', icon: Home, group: 'institution' },
  arbeitet_bei: { label: 'Arbeitet bei', color: '#F97316', icon: Briefcase, group: 'institution' },
  betreut_von: { label: 'Betreut von', color: '#A855F7', icon: Users, group: 'institution' },
  bank_bei: { label: 'Bankverbindung', color: '#EAB308', icon: Banknote, group: 'institution' },
  vermieter: { label: 'Vermieter', color: '#6366F1', icon: Home, group: 'institution' },
  angehoerig: { label: 'Angehörige', color: '#F43F5E', icon: Users, group: 'person' },
  behandelt_von: { label: 'Arzt/Behandlung', color: '#14B8A6', icon: Stethoscope, group: 'person' },
  rechtspfleger_fuer: { label: 'Rechtspfleger', color: '#64748B', icon: Scale, group: 'person' },
  sachbearbeiter_fuer: { label: 'Sachbearbeiter', color: '#78716C', icon: Briefcase, group: 'person' },
  ansprechpartner_fuer: { label: 'Ansprechpartner', color: '#84CC16', icon: Phone, group: 'person' },
  pflegekraft_fuer: { label: 'Pflegekraft', color: '#06B6D4', icon: Heart, group: 'person' },
  sonstiges: { label: 'Sonstige', color: '#9CA3AF', icon: Link2, group: 'other' }
};

// Gruppierte Beziehungsarten für bessere UX
const BEZIEHUNG_GRUPPEN = {
  institution: {
    label: 'Institutionen',
    arten: ['versichert_bei', 'leistung_von', 'schulden_bei', 'wohnt_in', 'arbeitet_bei', 'betreut_von', 'bank_bei', 'vermieter']
  },
  person: {
    label: 'Personen',
    arten: ['angehoerig', 'behandelt_von', 'rechtspfleger_fuer', 'sachbearbeiter_fuer', 'ansprechpartner_fuer', 'pflegekraft_fuer']
  },
  other: {
    label: 'Sonstige',
    arten: ['sonstiges']
  }
};

export default function NetzwerkGraph({ onNodeClick, onNavigate }) {
  const graphRef = useRef();
  const containerRef = useRef();
  
  const [rawGraphData, setRawGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
  
  // Einstellungen
  const [showLabels, setShowLabels] = useState(true);
  const [showOrphans, setShowOrphans] = useState(false);
  const [maxNodes, setMaxNodes] = useState(150);

  // Filter States
  const [activeNodeTypes, setActiveNodeTypes] = useState(new Set(['klient', 'juristische_person', 'natuerliche_person']));
  const [activeBeziehungen, setActiveBeziehungen] = useState(new Set(Object.keys(BEZIEHUNG_CONFIG)));
  const [filterOpen, setFilterOpen] = useState(false);

  // Dimensionen beim Mount und Resize aktualisieren
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width || 800,
          height: Math.max(500, window.innerHeight - 400)
        });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Graph-Daten laden
  const loadGraphData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/netzwerk/graph-daten?max_nodes=${maxNodes}&include_orphans=${showOrphans}`
      );
      const data = await res.json();
      
      setRawGraphData({
        nodes: data.nodes.map(n => ({
          ...n,
          val: n.size || 10
        })),
        links: data.links.map(l => ({
          ...l,
          value: 1
        }))
      });
    } catch (error) {
      console.error('Fehler beim Laden der Graph-Daten:', error);
      toast.error('Fehler beim Laden des Netzwerk-Graphen');
    } finally {
      setLoading(false);
    }
  }, [maxNodes, showOrphans]);

  useEffect(() => {
    loadGraphData();
  }, [loadGraphData]);

  // Gefilterte Graph-Daten berechnen
  const filteredGraphData = useMemo(() => {
    // Filtere Links nach Beziehungsart
    const filteredLinks = rawGraphData.links.filter(link => 
      activeBeziehungen.has(link.beziehung_art)
    );
    
    // Sammle alle Node-IDs die in gefilterten Links vorkommen
    const linkedNodeIds = new Set();
    filteredLinks.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      linkedNodeIds.add(sourceId);
      linkedNodeIds.add(targetId);
    });
    
    // Filtere Nodes nach Typ UND ob sie in gefilterten Links vorkommen
    const filteredNodes = rawGraphData.nodes.filter(node => {
      // Prüfe Knotentyp-Filter
      if (!activeNodeTypes.has(node.typ)) return false;
      
      // Wenn keine Filter aktiv sind, zeige alle
      if (activeBeziehungen.size === Object.keys(BEZIEHUNG_CONFIG).length) {
        return true;
      }
      
      // Zeige nur Nodes die in gefilterten Links vorkommen
      return linkedNodeIds.has(node.id);
    });
    
    // Filtere Links nochmal: beide Enden müssen in gefilterten Nodes sein
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const finalLinks = filteredLinks.filter(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      return nodeIds.has(sourceId) && nodeIds.has(targetId);
    });
    
    return {
      nodes: filteredNodes,
      links: finalLinks
    };
  }, [rawGraphData, activeNodeTypes, activeBeziehungen]);

  // Toggle Knotentyp-Filter
  const toggleNodeType = (type) => {
    setActiveNodeTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  // Toggle Beziehungsart-Filter
  const toggleBeziehung = (art) => {
    setActiveBeziehungen(prev => {
      const newSet = new Set(prev);
      if (newSet.has(art)) {
        newSet.delete(art);
      } else {
        newSet.add(art);
      }
      return newSet;
    });
  };

  // Alle Beziehungen einer Gruppe togglen
  const toggleBeziehungGruppe = (gruppe) => {
    const arten = BEZIEHUNG_GRUPPEN[gruppe].arten;
    const allActive = arten.every(art => activeBeziehungen.has(art));
    
    setActiveBeziehungen(prev => {
      const newSet = new Set(prev);
      if (allActive) {
        arten.forEach(art => newSet.delete(art));
      } else {
        arten.forEach(art => newSet.add(art));
      }
      return newSet;
    });
  };

  // Alle Filter zurücksetzen
  const resetFilters = () => {
    setActiveNodeTypes(new Set(['klient', 'juristische_person', 'natuerliche_person']));
    setActiveBeziehungen(new Set(Object.keys(BEZIEHUNG_CONFIG)));
  };

  // Anzahl aktiver Filter
  const activeFilterCount = useMemo(() => {
    const nodeFilters = 3 - activeNodeTypes.size;
    const beziehungFilters = Object.keys(BEZIEHUNG_CONFIG).length - activeBeziehungen.size;
    return nodeFilters + beziehungFilters;
  }, [activeNodeTypes, activeBeziehungen]);

  // Knoten-Canvas-Rendering
  const paintNode = useCallback((node, ctx, globalScale) => {
    const label = node.name;
    const fontSize = Math.max(10 / globalScale, 3);
    const nodeSize = node.val || 10;
    
    // Knoten-Kreis zeichnen
    ctx.beginPath();
    ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI);
    ctx.fillStyle = node.color || '#666';
    ctx.fill();
    
    // Highlight bei Hover/Selection
    if (hoveredNode?.id === node.id || selectedNode?.id === node.id) {
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3 / globalScale;
      ctx.stroke();
      
      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeSize + 3, 0, 2 * Math.PI);
      ctx.strokeStyle = node.color || '#666';
      ctx.lineWidth = 2 / globalScale;
      ctx.stroke();
    }
    
    // Label zeichnen
    if (showLabels && globalScale > 0.5) {
      ctx.font = `${fontSize}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      
      // Hintergrund für bessere Lesbarkeit
      const textWidth = ctx.measureText(label).width;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.fillRect(
        node.x - textWidth / 2 - 2,
        node.y + nodeSize + 2,
        textWidth + 4,
        fontSize + 4
      );
      
      ctx.fillStyle = '#334155';
      ctx.fillText(label, node.x, node.y + nodeSize + 4);
    }
  }, [hoveredNode, selectedNode, showLabels]);

  // Link-Rendering
  const paintLink = useCallback((link, ctx, globalScale) => {
    const start = link.source;
    const end = link.target;
    
    if (typeof start !== 'object' || typeof end !== 'object') return;
    
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.strokeStyle = link.color || '#9CA3AF';
    ctx.lineWidth = hoveredNode && 
      (link.source.id === hoveredNode.id || link.target.id === hoveredNode.id)
        ? 3 / globalScale
        : 1.5 / globalScale;
    ctx.stroke();
  }, [hoveredNode]);

  // Event Handler
  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    if (onNodeClick) {
      onNodeClick(node);
    }
  }, [onNodeClick]);

  const handleNodeHover = useCallback((node) => {
    setHoveredNode(node);
    if (containerRef.current) {
      containerRef.current.style.cursor = node ? 'pointer' : 'grab';
    }
  }, []);

  // Graph-Kontrollen
  const handleZoomIn = () => {
    if (graphRef.current) {
      graphRef.current.zoom(graphRef.current.zoom() * 1.3, 400);
    }
  };

  const handleZoomOut = () => {
    if (graphRef.current) {
      graphRef.current.zoom(graphRef.current.zoom() / 1.3, 400);
    }
  };

  const handleFitView = () => {
    if (graphRef.current) {
      graphRef.current.zoomToFit(400, 50);
    }
  };

  const handleReset = () => {
    if (graphRef.current) {
      graphRef.current.centerAt(0, 0, 400);
      graphRef.current.zoom(1, 400);
    }
  };

  // Demo-Daten erstellen
  const handleCreateDemoData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/netzwerk/demo-natuerliche-personen`, {
        method: 'POST'
      });
      const data = await res.json();
      toast.success(`${data.statistik.personen} Personen und ${data.statistik.verknuepfungen} Verknüpfungen erstellt`);
      loadGraphData();
    } catch (error) {
      toast.error('Fehler beim Erstellen der Demo-Daten');
    }
  };

  // Statistiken berechnen (gefiltert)
  const stats = useMemo(() => {
    return {
      klienten: filteredGraphData.nodes.filter(n => n.typ === 'klient').length,
      institutionen: filteredGraphData.nodes.filter(n => n.typ === 'juristische_person').length,
      personen: filteredGraphData.nodes.filter(n => n.typ === 'natuerliche_person').length,
      links: filteredGraphData.links.length,
      total: filteredGraphData.nodes.length
    };
  }, [filteredGraphData]);

  // Statistiken für aktive Beziehungsarten
  const beziehungStats = useMemo(() => {
    const counts = {};
    rawGraphData.links.forEach(link => {
      const art = link.beziehung_art;
      counts[art] = (counts[art] || 0) + 1;
    });
    return counts;
  }, [rawGraphData]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-lg border border-slate-200 p-3">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Zoom Controls */}
          <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
            <Button variant="ghost" size="sm" onClick={handleZoomIn} title="Vergrößern">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleZoomOut} title="Verkleinern">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleFitView} title="Ansicht anpassen">
              <Maximize2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleReset} title="Zurücksetzen">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>

          {/* Knotentyp-Filter */}
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1" data-testid="node-type-filter">
            <Button
              variant={activeNodeTypes.has('klient') ? 'default' : 'ghost'}
              size="sm"
              className={`h-7 px-2 ${activeNodeTypes.has('klient') ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
              onClick={() => toggleNodeType('klient')}
              title="Betreute anzeigen/ausblenden"
            >
              <Users className="w-4 h-4" />
            </Button>
            <Button
              variant={activeNodeTypes.has('juristische_person') ? 'default' : 'ghost'}
              size="sm"
              className={`h-7 px-2 ${activeNodeTypes.has('juristische_person') ? 'bg-teal-500 hover:bg-teal-600' : ''}`}
              onClick={() => toggleNodeType('juristische_person')}
              title="Institutionen anzeigen/ausblenden"
            >
              <Building2 className="w-4 h-4" />
            </Button>
            <Button
              variant={activeNodeTypes.has('natuerliche_person') ? 'default' : 'ghost'}
              size="sm"
              className={`h-7 px-2 ${activeNodeTypes.has('natuerliche_person') ? 'bg-purple-500 hover:bg-purple-600' : ''}`}
              onClick={() => toggleNodeType('natuerliche_person')}
              title="Personen anzeigen/ausblenden"
            >
              <UserCircle className="w-4 h-4" />
            </Button>
          </div>

          {/* Beziehungsart-Filter Dropdown */}
          <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="relative" data-testid="beziehung-filter-trigger">
                <Filter className="w-4 h-4 mr-2" />
                Beziehungen
                {activeFilterCount > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64" align="start" data-testid="beziehung-filter-content">
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="p-0">Beziehungsarten filtern</DropdownMenuLabel>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs"
                  onClick={resetFilters}
                >
                  Alle
                </Button>
              </div>
              <DropdownMenuSeparator />
              
              {/* Institutionen-Beziehungen */}
              <DropdownMenuLabel className="text-xs text-slate-500 flex items-center gap-2">
                <Building2 className="w-3 h-3" />
                Institutionen
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1.5 ml-auto text-xs"
                  onClick={(e) => { e.preventDefault(); toggleBeziehungGruppe('institution'); }}
                >
                  {BEZIEHUNG_GRUPPEN.institution.arten.every(a => activeBeziehungen.has(a)) ? 'Keine' : 'Alle'}
                </Button>
              </DropdownMenuLabel>
              {BEZIEHUNG_GRUPPEN.institution.arten.map(art => {
                const config = BEZIEHUNG_CONFIG[art];
                const Icon = config.icon;
                const count = beziehungStats[art] || 0;
                return (
                  <DropdownMenuCheckboxItem
                    key={art}
                    checked={activeBeziehungen.has(art)}
                    onCheckedChange={() => toggleBeziehung(art)}
                    className="cursor-pointer"
                    data-testid={`filter-${art}`}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: config.color }}
                      />
                      <span>{config.label}</span>
                      <span className="ml-auto text-xs text-slate-400">{count}</span>
                    </div>
                  </DropdownMenuCheckboxItem>
                );
              })}
              
              <DropdownMenuSeparator />
              
              {/* Personen-Beziehungen */}
              <DropdownMenuLabel className="text-xs text-slate-500 flex items-center gap-2">
                <UserCircle className="w-3 h-3" />
                Personen
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1.5 ml-auto text-xs"
                  onClick={(e) => { e.preventDefault(); toggleBeziehungGruppe('person'); }}
                >
                  {BEZIEHUNG_GRUPPEN.person.arten.every(a => activeBeziehungen.has(a)) ? 'Keine' : 'Alle'}
                </Button>
              </DropdownMenuLabel>
              {BEZIEHUNG_GRUPPEN.person.arten.map(art => {
                const config = BEZIEHUNG_CONFIG[art];
                const count = beziehungStats[art] || 0;
                return (
                  <DropdownMenuCheckboxItem
                    key={art}
                    checked={activeBeziehungen.has(art)}
                    onCheckedChange={() => toggleBeziehung(art)}
                    className="cursor-pointer"
                    data-testid={`filter-${art}`}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: config.color }}
                      />
                      <span>{config.label}</span>
                      <span className="ml-auto text-xs text-slate-400">{count}</span>
                    </div>
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Einstellungen */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="w-4 h-4 mr-2" />
                Einstellungen
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-labels" className="text-sm">Namen anzeigen</Label>
                  <Switch
                    id="show-labels"
                    checked={showLabels}
                    onCheckedChange={setShowLabels}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-orphans" className="text-sm">Unverknüpfte zeigen</Label>
                  <Switch
                    id="show-orphans"
                    checked={showOrphans}
                    onCheckedChange={setShowOrphans}
                  />
                </div>
                <div className="pt-2 border-t">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={handleCreateDemoData}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Demo-Personen erstellen
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          
          <Button variant="outline" size="sm" onClick={loadGraphData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>

          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="graph-export-btn">
                <Download className="w-4 h-4 mr-2" />
                Exportieren
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Graph exportieren</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={false}
                onCheckedChange={() => {
                  const ok = exportGraphAsPNG(containerRef.current, 'netzwerk-graph');
                  if (ok) toast.success('PNG erfolgreich exportiert');
                  else toast.error('PNG-Export fehlgeschlagen');
                }}
                data-testid="global-export-png"
              >
                Als PNG exportieren (Bild)
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={false}
                onCheckedChange={() => {
                  const ok = exportGraphAsSVG(filteredGraphData, {
                    width: Math.round(dimensions.width),
                    height: Math.round(dimensions.height),
                    title: 'Olympus Netzwerk-Graph',
                    filename: 'netzwerk-graph'
                  });
                  if (ok) toast.success('SVG erfolgreich exportiert');
                  else toast.error('SVG-Export fehlgeschlagen');
                }}
                data-testid="global-export-svg"
              >
                Als SVG exportieren (Vektor)
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Statistiken */}
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-slate-600">{stats.klienten} Betreute</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-teal-500"></div>
            <span className="text-slate-600">{stats.institutionen} Institutionen</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="text-slate-600">{stats.personen} Personen</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-400">|</div>
          <span className="text-slate-500">{stats.links} Verbindungen</span>
        </div>
      </div>

      {/* Aktive Filter Badges */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="active-filters">
          <span className="text-sm text-slate-500">Aktive Filter:</span>
          {!activeNodeTypes.has('klient') && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-700">
              Betreute ausgeblendet
              <button onClick={() => toggleNodeType('klient')} className="ml-1 hover:text-blue-900">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {!activeNodeTypes.has('juristische_person') && (
            <Badge variant="secondary" className="bg-teal-100 text-teal-700">
              Institutionen ausgeblendet
              <button onClick={() => toggleNodeType('juristische_person')} className="ml-1 hover:text-teal-900">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {!activeNodeTypes.has('natuerliche_person') && (
            <Badge variant="secondary" className="bg-purple-100 text-purple-700">
              Personen ausgeblendet
              <button onClick={() => toggleNodeType('natuerliche_person')} className="ml-1 hover:text-purple-900">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {Object.keys(BEZIEHUNG_CONFIG).filter(art => !activeBeziehungen.has(art)).slice(0, 3).map(art => (
            <Badge key={art} variant="secondary" className="bg-slate-100">
              {BEZIEHUNG_CONFIG[art].label} aus
              <button onClick={() => toggleBeziehung(art)} className="ml-1 hover:text-slate-900">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
          {Object.keys(BEZIEHUNG_CONFIG).filter(art => !activeBeziehungen.has(art)).length > 3 && (
            <Badge variant="secondary" className="bg-slate-100">
              +{Object.keys(BEZIEHUNG_CONFIG).filter(art => !activeBeziehungen.has(art)).length - 3} weitere
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={resetFilters} className="h-6 px-2 text-xs">
            Alle zurücksetzen
          </Button>
        </div>
      )}

      {/* Graph Container */}
      <div 
        ref={containerRef}
        className="relative bg-slate-50 rounded-xl border border-slate-200 overflow-hidden"
        style={{ height: dimensions.height }}
        data-testid="netzwerk-graph-container"
      >
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
            <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredGraphData.nodes.length === 0 ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Filter className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500">Keine Knoten mit aktiven Filtern</p>
            <p className="text-sm text-slate-400 mt-1">
              Passen Sie die Filter an oder klicken Sie auf "Alle zurücksetzen"
            </p>
            <Button variant="outline" size="sm" onClick={resetFilters} className="mt-4">
              Filter zurücksetzen
            </Button>
          </div>
        ) : (
          <ForceGraph2D
            ref={graphRef}
            width={dimensions.width}
            height={dimensions.height}
            graphData={filteredGraphData}
            nodeCanvasObject={paintNode}
            nodePointerAreaPaint={(node, color, ctx) => {
              ctx.beginPath();
              ctx.arc(node.x, node.y, node.val + 5, 0, 2 * Math.PI);
              ctx.fillStyle = color;
              ctx.fill();
            }}
            linkCanvasObject={paintLink}
            onNodeClick={handleNodeClick}
            onNodeHover={handleNodeHover}
            cooldownTicks={100}
            linkDirectionalArrowLength={0}
            linkWidth={1.5}
            backgroundColor="transparent"
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.3}
          />
        )}
        
        {/* Selected Node Popup */}
        {selectedNode && (
          <div 
            className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-10"
            data-testid="node-detail-popup"
          >
            <button 
              onClick={() => setSelectedNode(null)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-start gap-3">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${selectedNode.color}20` }}
              >
                {(() => {
                  const IconComponent = TYP_CONFIG[selectedNode.typ]?.icon;
                  return IconComponent ? (
                    <IconComponent 
                      className="w-6 h-6" 
                      style={{ color: selectedNode.color }}
                    />
                  ) : null;
                })()}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-800 truncate">
                  {selectedNode.name}
                </h3>
                <p className="text-sm text-slate-500 truncate">
                  {selectedNode.subtext}
                </p>
                <Badge 
                  variant="secondary" 
                  className={`mt-2 ${TYP_CONFIG[selectedNode.typ]?.bgClass || 'bg-gray-100'}`}
                >
                  {TYP_CONFIG[selectedNode.typ]?.label || selectedNode.typ}
                </Badge>
              </div>
            </div>
            
            <div className="mt-4 flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => {
                  if (onNavigate) {
                    onNavigate(selectedNode.typ, selectedNode.entity_id);
                  }
                  setSelectedNode(null);
                }}
              >
                <Eye className="w-4 h-4 mr-2" />
                Details
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  if (graphRef.current) {
                    graphRef.current.centerAt(selectedNode.x, selectedNode.y, 400);
                    graphRef.current.zoom(2, 400);
                  }
                }}
              >
                <Maximize2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Legende */}
        <div className="absolute top-3 left-3 bg-white/90 rounded-lg border border-slate-200 p-3 text-xs space-y-2">
          <p className="font-medium text-slate-700">Legende</p>
          <div className="space-y-1.5">
            {activeNodeTypes.has('klient') && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-slate-600">Betreute</span>
              </div>
            )}
            {activeNodeTypes.has('juristische_person') && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                <span className="text-slate-600">Institutionen</span>
              </div>
            )}
            {activeNodeTypes.has('natuerliche_person') && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-slate-600">Personen</span>
              </div>
            )}
          </div>
          {activeBeziehungen.size < Object.keys(BEZIEHUNG_CONFIG).length && (
            <>
              <div className="border-t border-slate-200 pt-2 mt-2">
                <p className="font-medium text-slate-600 mb-1">Beziehungen</p>
                <div className="space-y-1">
                  {Array.from(activeBeziehungen).slice(0, 5).map(art => (
                    <div key={art} className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: BEZIEHUNG_CONFIG[art]?.color }}
                      />
                      <span className="text-slate-500 text-[10px]">{BEZIEHUNG_CONFIG[art]?.label}</span>
                    </div>
                  ))}
                  {activeBeziehungen.size > 5 && (
                    <span className="text-slate-400 text-[10px]">+{activeBeziehungen.size - 5} weitere</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
